import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class RecurringMultiYearTaxAdjustmentService {
  
  constructor(private http: HttpClient) { }

  getGLAccounts(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    chart_key: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=aj9z5t`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('chart_key', chart_key);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  getTriggerData(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    combi_key: any,
    posting_acct_key: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=byc4kc`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('combi_key', combi_key);
    params.append('posting_acct_key', posting_acct_key);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  getTemplateData(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    sys_acct: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=yz4utp`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('sys_acct', sys_acct);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  getSchdMType(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    sys_acct: any,
    schdm_temp: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=jhsi7h`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('sys_acct', sys_acct);
    params.append('schdm_temp', schdm_temp);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  getTBBSData(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    local_acct: any,
    sys_acct: any,
    schdm_temp: any,
    source_system: any,
    schdm_type: any,
    combination_key: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=26k8ua`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('local_acct', local_acct);
    params.append('sys_acct', sys_acct);
    params.append('schdm_temp', schdm_temp);
    params.append('source_system', source_system);
    params.append('schdm_type', schdm_type);
    params.append('combination_key', combination_key);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  getExistingData(
    baseUrl: string,
    tax_year: any,
    scenario: any,
    jcd_key: any,
    cc_key: any,
    posting_acct_key: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=i3i6d0`;
    
    const params = new URLSearchParams();
    params.append('tax_year', tax_year);
    params.append('scenario', scenario);
    params.append('jcd_key', jcd_key);
    params.append('cc_key', cc_key);
    params.append('posting_acct_key', posting_acct_key);
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, params.toString(), { headers });
  }

  saveData(
    baseUrl: string,
    dataArray: any[],
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=ev11st`;
    
    const jsonObj = JSON.stringify(dataArray);
    const jsonSettings = JSON.stringify(dataArray);
    
    // ✅ CRITICAL: Build payload EXACTLY like original AngularJS
    // Use _.merge() and _.extend() like in AngularJS service
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add JSON data using _.extend() like AngularJS
    params = _.extend({ jsonObj: jsonObj }, params);
    params = _.extend({ jsonSettings: jsonSettings }, params);
    
    // ✅ CRITICAL: Add required configuration parameters with proper fallbacks
    // These are REQUIRED by backend and must be present
    params.tax_year = params.tax_year || filterParams?.tax_year || globalService?.globalParams?.tax_year || new Date().getFullYear();
    params.scenario = params.scenario || filterParams?.scenario || globalService?.globalParams?.scenario || 74;
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || globalService?.globalParams?.jcd_key || 250;
    
    // ✅ CRITICAL: scenarioCode - check multiple possible sources
    params.scenarioCode = params.scenarioCode || 
                         filterParams?.scenarioCode || 
                         filterParams?.scenario_code || 
                         globalService?.globalParams?.scenarioCode || 
                         globalService?.globalParams?.scenario_code || 
                         'RAF';
    
    // ✅ CRITICAL: is_issue_key_enabled - always required
    params.is_issue_key_enabled = params.is_issue_key_enabled || 
                                  filterParams?.is_issue_key_enabled || 
                                  globalService?.globalParams?.is_issue_key_enabled || 
                                  'N';
    
    // ✅ CRITICAL: Add other required fields from original payload
    params.screen_key = params.screen_key || filterParams?.screen_key || 10001;
    params.jcd_ta_key = params.jcd_ta_key || filterParams?.jcd_ta_key || 1202;
    params.activity_key = params.activity_key || filterParams?.activity_key || 949;
    params.screen_type = params.screen_type || filterParams?.screen_type || 'grid-dynamic';
    params.is_locked = params.is_locked || filterParams?.is_locked || 'N';
    params.issue_id = params.issue_id || filterParams?.issue_id || '';
    params.issue_short_desc = params.issue_short_desc || filterParams?.issue_short_desc || '';
    params.issue_id_for_tx_save = params.issue_id_for_tx_save || filterParams?.issue_id_for_tx_save || '';
    params.filing_key = params.filing_key || filterParams?.filing_key || 1;
    params.parent_me_string = params.parent_me_string || filterParams?.parent_me_string || '';
    params.process_name = 'PREDEFINED_SCHDM_ADJ';
    
    console.log('recurring multi-year params to save:', params);
    
    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        formData.append(key, params[key].toString());
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(url, formData.toString(), { headers });
  }
}

